import { CAM_SIZE, CamLayout, Source, uid } from './types'

/**
 * RecordingEngine owns every live piece of hardware for a session.
 *
 * The trick that makes Bloom work: instead of recording the screen stream
 * directly, we draw the screen onto a <canvas> every frame, draw the camera
 * cutout on top of it, and record *the canvas*. That means the face bubble's
 * position and size are baked into the pixels of the final video — and can be
 * changed at any moment, including mid-recording.
 *
 * Streams stay alive between takes so "record more" doesn't re-prompt for
 * screen-share permission.
 */
export class RecordingEngine {
  readonly canvas = document.createElement('canvas')
  private ctx = this.canvas.getContext('2d', { alpha: false })!

  private screenVideo = makeVideoEl()
  private camVideo = makeVideoEl()

  private screenStream: MediaStream | null = null
  private camStream: MediaStream | null = null
  private micStream: MediaStream | null = null

  private audioCtx: AudioContext | null = null
  private mixDest: MediaStreamAudioDestinationNode | null = null
  private micNode: MediaStreamAudioSourceNode | null = null
  private sysNode: MediaStreamAudioSourceNode | null = null
  private micGain: GainNode | null = null
  private sysGain: GainNode | null = null

  private recorder: MediaRecorder | null = null
  private chunks: Blob[] = []
  private rafId = 0

  /** Live mutable layout — the UI writes straight into this, no re-render needed. */
  layout: CamLayout = { x: 0.86, y: 0.8, mirror: true, hidden: false }

  /** Wall-clock bookkeeping so we know a take's true duration (WebM won't tell us). */
  private takeStartedAt = 0
  private pausedTotal = 0
  private pausedAt = 0

  state: 'idle' | 'recording' | 'paused' = 'idle'

  onScreenEnded: (() => void) | null = null
  onMicLevel: ((level: number) => void) | null = null

  // ---------------------------------------------------------------- sources

  async attachScreen(stream: MediaStream) {
    this.screenStream?.getTracks().forEach((t) => t.stop())
    this.screenStream = stream
    this.screenVideo.srcObject = stream
    await this.screenVideo.play().catch(() => {})

    const track = stream.getVideoTracks()[0]
    track.addEventListener('ended', () => this.onScreenEnded?.())

    const settings = track.getSettings()
    // Cap at 1080p-ish: a 5K display would otherwise melt the encoder.
    const rawW = settings.width || this.screenVideo.videoWidth || 1920
    const rawH = settings.height || this.screenVideo.videoHeight || 1080
    const scale = Math.min(1, 1920 / rawW, 1080 / rawH)
    this.canvas.width = Math.round((rawW * scale) / 2) * 2
    this.canvas.height = Math.round((rawH * scale) / 2) * 2

    // System audio arrives on the display stream when the user ticked the box.
    const sysTrack = stream.getAudioTracks()[0]
    this.ensureAudioGraph()
    this.sysNode?.disconnect()
    this.sysNode = null
    if (sysTrack) {
      this.sysNode = this.audioCtx!.createMediaStreamSource(new MediaStream([sysTrack]))
      this.sysNode.connect(this.sysGain!)
    }
    this.startDrawLoop()
  }

  async attachCamera(stream: MediaStream | null) {
    this.camStream?.getTracks().forEach((t) => t.stop())
    this.camStream = stream
    this.camVideo.srcObject = stream
    if (stream) await this.camVideo.play().catch(() => {})
  }

  async attachMic(stream: MediaStream | null) {
    this.micStream?.getTracks().forEach((t) => t.stop())
    this.micStream = stream
    this.ensureAudioGraph()
    this.micNode?.disconnect()
    this.micNode = null
    if (stream) {
      this.micNode = this.audioCtx!.createMediaStreamSource(stream)
      this.micNode.connect(this.micGain!)
      this.watchMicLevel(stream)
    }
  }

  get hasScreen() { return !!this.screenStream?.getVideoTracks()[0]?.readyState }
  get frameSize() { return { width: this.canvas.width, height: this.canvas.height } }

  setMicMuted(muted: boolean) { if (this.micGain) this.micGain.gain.value = muted ? 0 : 1 }
  setSystemAudioMuted(muted: boolean) { if (this.sysGain) this.sysGain.gain.value = muted ? 0 : 0.85 }

  // ------------------------------------------------------------------ audio

  private ensureAudioGraph() {
    if (this.audioCtx) return
    this.audioCtx = new AudioContext()
    this.mixDest = this.audioCtx.createMediaStreamDestination()
    this.micGain = this.audioCtx.createGain()
    this.sysGain = this.audioCtx.createGain()
    this.micGain.gain.value = 1
    this.sysGain.gain.value = 0.85
    this.micGain.connect(this.mixDest)
    this.sysGain.connect(this.mixDest)

    // A permanently-silent oscillator guarantees the mix always carries an audio
    // track. Without it, a mic-less recording would produce a video the server's
    // concat step can't line up against takes that *do* have sound.
    const silence = this.audioCtx.createConstantSource()
    const mute = this.audioCtx.createGain()
    mute.gain.value = 0
    silence.connect(mute).connect(this.mixDest)
    silence.start()
  }

  private watchMicLevel(stream: MediaStream) {
    if (!this.audioCtx) return
    const analyser = this.audioCtx.createAnalyser()
    analyser.fftSize = 512
    this.audioCtx.createMediaStreamSource(stream).connect(analyser)
    const buf = new Uint8Array(analyser.frequencyBinCount)
    const tick = () => {
      if (this.micStream !== stream) return // a newer mic took over
      analyser.getByteTimeDomainData(buf)
      let peak = 0
      for (const v of buf) peak = Math.max(peak, Math.abs(v - 128) / 128)
      this.onMicLevel?.(peak)
      setTimeout(tick, 100)
    }
    tick()
  }

  // ----------------------------------------------------------------- drawing

  private startDrawLoop() {
    if (this.rafId) return
    const draw = () => {
      this.rafId = requestAnimationFrame(draw)
      this.drawFrame()
    }
    this.rafId = requestAnimationFrame(draw)
  }

  private drawFrame() {
    const { ctx, canvas } = this
    const W = canvas.width
    const H = canvas.height
    ctx.fillStyle = '#000'
    ctx.fillRect(0, 0, W, H)

    if (this.screenVideo.readyState >= 2) {
      drawCover(ctx, this.screenVideo, 0, 0, W, H)
    }

    const l = this.layout
    if (!l.hidden && this.camVideo.readyState >= 2 && this.camStream) {
      const w = Math.max(48, CAM_SIZE * W)
      const h = w
      const cx = l.x * W
      const cy = l.y * H
      const x = cx - w / 2
      const y = cy - h / 2
      const radius = w / 2

      ctx.save()
      // Soft drop shadow so the bubble reads as floating above the screen.
      ctx.shadowColor = 'rgba(0,0,0,0.45)'
      ctx.shadowBlur = w * 0.12
      ctx.shadowOffsetY = w * 0.04
      ctx.fillStyle = 'rgba(0,0,0,0.5)'
      roundedPath(ctx, x, y, w, h, radius)
      ctx.fill()
      ctx.restore()

      ctx.save()
      roundedPath(ctx, x, y, w, h, radius)
      ctx.clip()
      if (l.mirror) {
        ctx.translate(cx, 0)
        ctx.scale(-1, 1)
        ctx.translate(-cx, 0)
      }
      drawCover(ctx, this.camVideo, x, y, w, h)
      ctx.restore()

      ctx.save()
      ctx.lineWidth = Math.max(2, w * 0.012)
      ctx.strokeStyle = 'rgba(255,255,255,0.85)'
      roundedPath(ctx, x, y, w, h, radius)
      ctx.stroke()
      ctx.restore()
    }
  }

  // ---------------------------------------------------------------- take I/O

  startTake() {
    if (this.state !== 'idle') return
    if (!this.mixDest) this.ensureAudioGraph()
    void this.audioCtx?.resume()

    const canvasStream = this.canvas.captureStream(30)
    for (const t of this.mixDest!.stream.getAudioTracks()) canvasStream.addTrack(t)

    const mimeType = pickMimeType()
    this.recorder = new MediaRecorder(canvasStream, {
      mimeType,
      videoBitsPerSecond: 5_000_000,
      audioBitsPerSecond: 128_000,
    })
    this.chunks = []
    this.recorder.ondataavailable = (e) => { if (e.data.size) this.chunks.push(e.data) }
    this.recorder.start(1000)

    this.takeStartedAt = performance.now()
    this.pausedTotal = 0
    this.pausedAt = 0
    this.state = 'recording'
  }

  pause() {
    if (this.state !== 'recording' || !this.recorder) return
    this.recorder.pause()
    this.pausedAt = performance.now()
    this.state = 'paused'
  }

  resume() {
    if (this.state !== 'paused' || !this.recorder) return
    this.recorder.resume()
    this.pausedTotal += performance.now() - this.pausedAt
    this.pausedAt = 0
    this.state = 'recording'
  }

  /** Seconds of actual recorded material in the current take (pauses excluded). */
  elapsed() {
    if (this.state === 'idle') return 0
    const now = this.pausedAt || performance.now()
    return (now - this.takeStartedAt - this.pausedTotal) / 1000
  }

  stopTake(): Promise<Source> {
    return new Promise((resolve, reject) => {
      const rec = this.recorder
      if (!rec || this.state === 'idle') return reject(new Error('Not recording'))
      const duration = this.elapsed()
      rec.onstop = () => {
        const blob = new Blob(this.chunks, { type: rec.mimeType || 'video/webm' })
        this.chunks = []
        this.recorder = null
        this.state = 'idle'
        resolve({
          id: uid(),
          blob,
          url: URL.createObjectURL(blob),
          duration,
          width: this.canvas.width,
          height: this.canvas.height,
          recordedAt: Date.now(),
        })
      }
      if (this.state === 'paused') rec.resume()
      rec.stop()
    })
  }

  /** Release every device. Call when the user is completely finished. */
  dispose() {
    cancelAnimationFrame(this.rafId)
    this.rafId = 0
    ;[this.screenStream, this.camStream, this.micStream].forEach((s) =>
      s?.getTracks().forEach((t) => t.stop()),
    )
    this.screenStream = this.camStream = this.micStream = null
    void this.audioCtx?.close()
    this.audioCtx = null
  }

  /** A camera-only stream for the floating window's live preview. */
  get cameraStream() { return this.camStream }
}

// ------------------------------------------------------------------ helpers

function makeVideoEl() {
  const v = document.createElement('video')
  v.muted = true
  v.playsInline = true
  v.autoplay = true
  return v
}

/** Draw `src` into the box, cropping the overflow — CSS `object-fit: cover`. */
function drawCover(
  ctx: CanvasRenderingContext2D,
  src: HTMLVideoElement,
  x: number, y: number, w: number, h: number,
) {
  const sw = src.videoWidth
  const sh = src.videoHeight
  if (!sw || !sh) return
  const scale = Math.max(w / sw, h / sh)
  const cw = w / scale
  const ch = h / scale
  ctx.drawImage(src, (sw - cw) / 2, (sh - ch) / 2, cw, ch, x, y, w, h)
}

function roundedPath(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number, r: number,
) {
  const rr = Math.min(r, w / 2, h / 2)
  ctx.beginPath()
  ctx.moveTo(x + rr, y)
  ctx.arcTo(x + w, y, x + w, y + h, rr)
  ctx.arcTo(x + w, y + h, x, y + h, rr)
  ctx.arcTo(x, y + h, x, y, rr)
  ctx.arcTo(x, y, x + w, y, rr)
  ctx.closePath()
}

function pickMimeType() {
  const candidates = [
    'video/webm;codecs=vp9,opus',
    'video/webm;codecs=vp8,opus',
    'video/webm',
    'video/mp4',
  ]
  return candidates.find((t) => MediaRecorder.isTypeSupported(t)) || ''
}
